import { useScrollReveal, animStyle } from '../../../hooks/useScrollReveal';

const steps = [
  {
    num: '01',
    title: 'お問い合わせ',
    desc: 'まずは気軽にご相談ください。どんな些細なご要望でもお気軽にフォームまたはお電話でご連絡ください。',
    icon: 'ri-mail-send-line',
    tag: 'まずここから',
  },
  {
    num: '02',
    title: 'ヒアリング・現状分析',
    desc: '貴社のビジネス目標・現在のSNS状況・課題・ターゲット層を丁寧にヒアリングし、現状を把握します。',
    icon: 'ri-survey-line',
    tag: '無料相談',
  },
  {
    num: '03',
    title: '戦略立案・ご提案',
    desc: '課題に合わせた最適な施策・KPI・スケジュール・予算感をご提案。ご納得いただけるまで調整します。',
    icon: 'ri-lightbulb-flash-line',
    tag: 'カスタム提案',
  },
  {
    num: '04',
    title: '運用開始',
    desc: 'ご契約後、迅速に体制を整えて運用・広告・コンテンツ制作を実行。担当チームが貴社を専任サポート。',
    icon: 'ri-rocket-line',
    tag: 'スピード対応',
  },
  {
    num: '05',
    title: '効果測定・継続改善',
    desc: 'データに基づいた定期レポートを提出。PDCAを高速で回し、成果を継続的に最大化していきます。',
    icon: 'ri-bar-chart-grouped-line',
    tag: '月次レポート',
  },
];

export default function HowItWorks() {
  const { ref: headerRef, visible: headerVisible } = useScrollReveal(0.3);
  const { ref: stepsRef, visible: stepsVisible } = useScrollReveal(0.1);

  return (
    <section id="flow" className="relative w-full bg-[#111] py-14 md:py-24 px-5 sm:px-8 md:px-20">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10">
        {/* Section header */}
        <div ref={headerRef} className="mb-10 md:mb-16">
          <div
            style={{
              width: headerVisible ? '32px' : '0px',
              height: '2px',
              backgroundColor: '#fff',
              marginBottom: '24px',
              transition: 'width 0.7s ease-out',
            }}
          />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2
                className="text-5xl md:text-6xl font-bold text-white tracking-widest font-['Montserrat']"
                style={animStyle(headerVisible, 'fromLeft', 150, 750)}
              >
                HOW IT WORKS
              </h2>
              <p
                className="text-white/50 text-sm mt-3 tracking-wider"
                style={animStyle(headerVisible, 'fromLeft', 280, 700)}
              >
                ご相談から運用開始まで、スムーズにサポートします
              </p>
            </div>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 text-white/60 text-xs tracking-widest border border-white/20 px-5 py-2.5 hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap self-start"
              style={animStyle(headerVisible, 'fromRight', 200, 700)}
            >
              まずは無料相談
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="relative">
          {/* Connecting line — desktop only */}
          <div
            className="hidden lg:block absolute top-[52px] left-0 right-0 h-px"
            style={{
              background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.15) 10%, rgba(255,255,255,0.15) 90%, transparent)',
            }}
          />

          {/* Mobile: vertical list */}
          <div className="flex flex-col gap-0 lg:hidden">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 py-5 border-b border-white/8 last:border-b-0"
                style={animStyle(stepsVisible, 'fromLeft', idx * 90, 650)}
              >
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center bg-[#0d0d0d]">
                    <i className={`${step.icon} text-sm text-white/60`} />
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="w-px h-full min-h-[24px] bg-white/10 mt-1" />
                  )}
                </div>
                <div className="pt-1 pb-2 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white/30 text-[9px] tracking-[0.2em] font-['Montserrat']">STEP {step.num}</span>
                    <span className="text-white/40 text-[9px] border border-white/10 px-1.5 py-0.5 tracking-wider">{step.tag}</span>
                  </div>
                  <h3 className="text-white text-sm font-medium tracking-wide mb-1">{step.title}</h3>
                  <p className="text-white/45 text-xs leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: circle grid */}
          <div className="hidden lg:grid lg:grid-cols-5 gap-0">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="relative flex flex-col items-center text-center px-6 py-8 group"
                style={animStyle(stepsVisible, 'up', idx * 110, 700)}
              >
                <div className="relative mb-6">
                  <div className="w-[104px] h-[104px] rounded-full border border-white/15 flex items-center justify-center bg-[#0d0d0d] group-hover:border-white/40 transition-all duration-300 relative z-10">
                    <div className="flex flex-col items-center">
                      <i className={`${step.icon} text-xl text-white/50 group-hover:text-white/80 transition-colors duration-300 mb-1`} />
                      <span className="text-white/30 text-[10px] font-['Montserrat'] tracking-widest group-hover:text-white/50 transition-colors duration-300">
                        STEP {step.num}
                      </span>
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-20">
                      <i className="ri-arrow-right-s-line text-white/20 text-lg" />
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-white/40 tracking-[0.2em] border border-white/10 px-2.5 py-0.5 mb-3 font-['Montserrat'] group-hover:border-white/25 group-hover:text-white/60 transition-all duration-300">
                  {step.tag}
                </span>
                <h3 className="text-white text-sm font-medium tracking-wider mb-3 leading-snug">{step.title}</h3>
                <p className="text-white/45 text-xs leading-relaxed group-hover:text-white/65 transition-colors duration-300">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom note */}
        <div
          className="mt-10 md:mt-12 flex items-center justify-center gap-3"
          style={animStyle(stepsVisible, 'fade', 700, 700)}
        >
          <div className="h-px w-6 md:w-8 bg-white/15" />
          <p className="text-white/30 text-xs tracking-wider text-center">
            まずは気軽なご相談から。費用や内容のご質問だけでも歓迎しております。
          </p>
          <div className="h-px w-6 md:w-8 bg-white/15" />
        </div>
      </div>
    </section>
  );
}
